import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type Wallet, type WalletTransaction } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { IcEye, IcEyeOff, IcArrowDown, IcPlus } from '@/components/ui/icons';
import { BalanceFormatter } from './BalanceFormatter';
import { paths } from '@/routes/paths';
import styles from './WalletCard.module.css';

interface WalletCardProps {
  wallet?: Wallet | null;
  loading?: boolean;
  lastTx?: WalletTransaction | null;
  showAllAction?: boolean;
}

export function WalletCard({ wallet, loading, lastTx, showAllAction = true }: WalletCardProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);

  return (
    <div className={styles.wrap}>
      <div className={styles.topRow}>
        <span className={styles.label}>{t('home.available_balance')}</span>
        <button
          type="button"
          className={styles.eye}
          aria-label={hidden ? 'Show balance' : 'Hide balance'}
          onClick={() => setHidden((v) => !v)}
        >
          {hidden ? <IcEye /> : <IcEyeOff />}
        </button>
      </div>

      <div className={styles.balanceRow}>
        <div className={styles.balance}>
          {loading ? '—' : <BalanceFormatter cents={wallet?.available_cents ?? 0} hidden={hidden} />}{' '}
          <span className={styles.currency}>USD</span>
        </div>
        <Button
          variant="inverted"
          size="sm"
          startIcon={<IcPlus width={14} height={14} />}
          onClick={() => navigate(paths.walletTopup)}
        >
          {t('home.replenish')}
        </Button>
      </div>
      <div className={styles.miles}>
        ✈ {hidden ? '••• ••' : (wallet?.miles_balance ?? 0).toLocaleString()} {t('home.miles_suffix')}
      </div>

      {lastTx && (
        <div className={styles.txRow}>
          <span className={styles.txLabel}>{t('home.last_transaction')}</span>
          {showAllAction && (
            <button
              className={styles.allLink}
              type="button"
              onClick={() => navigate(paths.wallet)}
            >
              {t('common.all')}
            </button>
          )}
        </div>
      )}
      {lastTx && (
        <div className={styles.tx}>
          <span className={styles.txIcon}>
            <IcArrowDown />
          </span>
          <div className={styles.txBody}>
            <div className={styles.txTitle}>{lastTx.description}</div>
            <div className={styles.txDate}>{new Date(lastTx.occurred_at).toLocaleDateString()}</div>
          </div>
          <div className={styles.txAmount}>
            {lastTx.amount_cents >= 0 ? '+' : ''}
            <BalanceFormatter cents={lastTx.amount_cents} />
          </div>
        </div>
      )}
    </div>
  );
}
